import { useIntl } from 'react-intl';
import { PricingModelEnum } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';

import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { CheckboxGroup, type CheckboxItem } from '../CheckboxGroup';

type FilterEntry = { id: string; value: string[] };

type Props = {
  onFilter?: (filters: FilterEntry[]) => void;
  value?: string[];
};

export default function MediaType({ onFilter, value = [] }: Props) {
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser).toJSON();

  const options: CheckboxItem[] = [
    {
      id: PricingModelEnum['f-cpm'],
      label: formatMessage({
        defaultMessage: 'F-CPM',
        id: 'lineItems.advancedFilters.pricingType.fcpm'
      }),
      show: user.pricingModels.openRtb.includes(PricingModelEnum['f-cpm']),
      tooltipText: formatMessage({
        defaultMessage:
          'Fixed Cost Per Mille. A guaranteed, flat rate paid per 1,000 impressions. Unlike standard CPM, which fluctuates constantly in real-time bidding auctions, F-CPM is usually negotiated directly between an advertiser and a publisher to lock in a specific price.',
        id: 'pricingModel.pricingModels.f-cpm.tooltip'
      })
    },
    {
      id: PricingModelEnum['d-cpm'],
      label: formatMessage({
        defaultMessage: 'D-CPM',
        id: 'lineItems.advancedFilters.pricingType.dcpm'
      }),
      show: user.pricingModels.openRtb.includes(PricingModelEnum['d-cpm']),
      tooltipText: formatMessage({
        defaultMessage:
          'Dynamic Cost Per Mille (Cost Per 1,000 impressions). The value set is a maximum bid; Sonata bids up to it, and the effective CPM at the end of the campaign ranges from zero to that maximum. The impressions obtained vary with supply and demand on the ad exchanges.',
        id: 'pricingModel.pricingModels.d-cpm.tooltip'
      })
    },
    {
      id: PricingModelEnum.ppc,
      label: formatMessage({
        defaultMessage: 'CPC',
        id: 'pricingModel.pricingModels.cpc'
      }),
      show: user.pricingModels.openRtb.includes(PricingModelEnum.ppc),
      tooltipText: formatMessage({
        defaultMessage:
          'Cost Per Click. You pay only when a user actively clicks on your advertisement, regardless of how many times the ad was displayed.',
        id: 'pricingModel.pricingModels.cpc.tooltip'
      })
    },
    {
      id: PricingModelEnum.ppi,
      label: formatMessage({
        defaultMessage: 'CPI',
        id: 'pricingModel.pricingModels.cpi'
      }),
      show: user.pricingModels.openRtb.includes(PricingModelEnum.ppi),
      tooltipText: formatMessage({
        defaultMessage:
          'Cost Per Install. You pay only when a user clicks the ad and successfully downloads and installs your application.',
        id: 'pricingModel.pricingModels.cpi.tooltip'
      })
    },
    {
      id: PricingModelEnum.ppv,
      label: formatMessage({
        defaultMessage: 'CPV',
        id: 'pricingModel.pricingModels.cpv'
      }),
      show: user.pricingModels.openRtb.includes(PricingModelEnum.ppv),
      tooltipText: formatMessage({
        defaultMessage:
          'Cost Per View. Specifically for video advertising; you pay only when a user watches a minimum duration of your video or interacts with it.',
        id: 'pricingModel.pricingModels.cpv.tooltip'
      })
    },
    {
      id: PricingModelEnum.ppal,
      label: formatMessage({
        defaultMessage: 'CPA',
        id: 'pricingModel.pricingModels.cpa'
      }),
      show: user.pricingModels.openRtb.includes(PricingModelEnum.ppal),
      tooltipText: formatMessage({
        defaultMessage:
          'Cost Per Action. You pay only when a user completes a specific goal (a highly valuable action, such as making a purchase, submitting a lead form, or signing up for a trial).',
        id: 'pricingModel.pricingModels.cpa.tooltip'
      })
    },
    {
      id: PricingModelEnum.ppe,
      label: formatMessage({
        defaultMessage: 'CPE',
        id: 'pricingModel.pricingModels.cpe'
      }),
      show: user.pricingModels.openRtb.includes(PricingModelEnum.ppe),
      tooltipText: formatMessage({
        defaultMessage:
          'Cost Per Engagement. You pay only when a user actively engages with the ad unit. An "engagement" could mean expanding a banner, swiping through a carousel, playing an in-ad mini-game, or answering a poll.',
        id: 'pricingModel.pricingModels.cpe.tooltip'
      })
    },
    {
      id: PricingModelEnum.pppv,
      label: formatMessage({
        defaultMessage: 'CPPV',
        id: 'pricingModel.pricingModels.cppv'
      }),
      show: user.pricingModels.openRtb.includes(PricingModelEnum.pppv),
      tooltipText: formatMessage({
        defaultMessage:
          'Cost Per Page View. A step further than Cost Per Click (CPC). You pay only when a user clicks your ad and your destination website actually fully loads and is viewed. This protects your budget from accidental clicks or users who bounce before the site renders.',
        id: 'pricingModel.pricingModels.cppv.tooltip'
      })
    },
    {
      id: PricingModelEnum.ppsv,
      label: formatMessage({
        defaultMessage: 'CPSV',
        id: 'pricingModel.pricingModels.cpsv'
      }),
      show: user.pricingModels.openRtb.includes(PricingModelEnum.ppsv),
      tooltipText: formatMessage({
        defaultMessage:
          'Cost Per Second Viewed. You pay based on the actual duration (measured in seconds) that a user spends viewing your video or listening to your audio ad, ensuring you only pay for engaged time.',
        id: 'pricingModel.pricingModels.cpsv.tooltip'
      })
    }
  ];

  const handleChange = (_id: string, selected: string[]) => {
    const filter: FilterEntry[] = [{ id: 'pricingType', value: selected }];
    onFilter?.(filter);
  };

  return (
    <CheckboxGroup
      id="pricingType"
      onChange={handleChange}
      options={options}
      selected={value}
      title={formatMessage({
        defaultMessage: 'Pricing Type',
        id: 'campaigns.lineItems.advancedFilters.pricingType.title'
      })}
    />
  );
}
